import Box from '@cavilha/box';
import Flex from '@cavilha/flex';
import Text from '@cavilha/text';
import { useListFilter } from '@hooks/algolia/useListFilter';
import Image from 'next/image';
import { useState } from 'react';
import { connectRefinementList } from 'react-instantsearch-dom';

type VisualFiltersProps = {
  attribute: string;
  filters: Record<string, any>;
  items: Record<string, any>;
  refine: Function;
  limit?: number;
  showMore?: boolean;
  threesteps?: boolean;
  indexPosition?: number;
};

function VisualFilters({
  attribute,
  refine,
  filters,
  items,
  limit = 6,
  showMore = true,
  threesteps = false,
  indexPosition,
}: VisualFiltersProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { trackListFilter } = useListFilter();
  const parseAttributeLabel = (label: string) => {
    if (attribute.endsWith('(kg)')) {
      return `${label} kg`;
    }
    if (label === 'true') {
      return 'Sim';
    }
    if (label === 'false') {
      return 'Não';
    }
    return label;
  };

  return (
    <>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridGap: '$small2',
          mb: '$small3',
        }}
      >
        {items.map(
          ({ label, isRefined, value }, i: number) =>
            (i < limit || isExpanded) && (
              <Flex
                role="button"
                key={label}
                direction="column"
                justifyContent="start"
                alignItems="center"
                css={{
                  border: `${isRefined ? '1px solid $secondary4' : '1px solid $neutral3'}`,
                  p: '$small1',
                  br: '$medium',
                  textAlign: 'center',
                  '&:hover': {
                    border: '1px solid $secondary4',
                    cursor: 'pointer',
                  },
                }}
                onClick={e => {
                  e.preventDefault();
                  trackListFilter({
                    typeName: attribute,
                    elementPosition: i + 1,
                    elementName: label,
                    isThreeSteps: threesteps,
                    typeFixedPosition: indexPosition,
                  });
                  refine(value);
                }}
              >
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  css={{
                    width: '73px',
                    height: '73px',
                    bc: filters[label]?.responsiveImage ? 'none' : '$neutral2',
                    '& div': { maxWidth: '100%', maxHeight: '100%' },
                    '& img': {
                      maxWidth: '100%',
                      maxHeight: '100%',
                      left: '0',
                      right: '0',
                      margin: '$none auto',
                    },
                    '@md': {
                      width: '55px',
                      height: '55px',
                    },
                    '@lg': {
                      width: '73px',
                      height: '73px',
                    },
                  }}
                >
                  {filters[label]?.responsiveImage && (
                    <picture>
                      <Image
                        alt=""
                        role="presentation"
                        src={filters[label].responsiveImage.src}
                        width={55}
                        height={55}
                        blurDataURL={filters[label].responsiveImage.base64}
                      />
                    </picture>
                  )}
                </Flex>
                <Text as="p" size="bodyXSmall" css={{ overflowWrap: 'break-word' }}>
                  {parseAttributeLabel(label)}
                </Text>
              </Flex>
            )
        )}
      </Box>

      {showMore && items.length > limit && (
        <Text
          as="a"
          role="button"
          textStyle="bodySmallSemibold"
          css={{
            cursor: 'pointer',
            color: '$secondary4',
          }}
          onClick={e => {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? 'Ver menos' : 'Ver mais'}
        </Text>
      )}
    </>
  );
}

export default connectRefinementList(VisualFilters);
